import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  MessageCircle,
  Users,
  CalendarPlus,
  Facebook,
  Instagram,
  TrendingUp,
  TrendingDown,
  PenSquare,
  LayoutTemplate,
  ChevronRight,
  Activity,
  AlertCircle,
  Trash2,
  Loader2,
  RefreshCw
} from 'lucide-react';
import api from '../../../services/api';

export default function SocialMediaDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading]             = useState(true);
  // Set of post IDs currently being verified in background
  const [verifyingIds, setVerifyingIds]   = useState(new Set());
  const [accounts, setAccounts]           = useState([]);
  const [stats, setStats]                 = useState(null);
  const [posts, setPosts]                 = useState([]);
  const [comments, setComments]           = useState([]);
  const [error, setError]                 = useState(null);
  const [isSyncing, setIsSyncing]         = useState(false);

  const VERIFY_TTL_KEY = 'sm_last_verified';
  const VERIFY_TTL_MS  = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (isManualSync = false) => {
    try {
      if (isManualSync) {
        setIsSyncing(true);
      } else {
        setLoading(true);
      }
      const [accountsRes, statsRes, postsRes, commentsRes] = await Promise.all([
        api.get('/api/social-media/accounts').catch(() => ({ data: [] })),
        api.get('/api/social-media/analytics/overview').catch(() => ({ data: null })),
        api.get('/api/social-media/posts?limit=5').catch(() => ({ data: [] })),
        api.get('/api/social-media/comments?status=unread&limit=3').catch(() => ({ data: [] }))
      ]);

      const formatTimeAgo = (dateStr) => {
        if (!dateStr) return '';
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1)  return 'Just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24)  return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
      };

      setAccounts(accountsRes.data || []);
      setStats(statsRes.data || null);
      const fetchedPosts = postsRes.data || [];
      const mappedPosts = fetchedPosts.map(post => ({
        ...post,
        date: new Date(post.published_at || post.scheduled_at || post.created_at).toLocaleDateString()
      }));
      setPosts(mappedPosts);
      
      const mappedComments = (commentsRes.data || []).map(c => ({
        id: c.id,
        author: c.author_name || 'Facebook User',
        avatar: c.author_avatar_url || null,
        platform: c.platform,
        text: c.message || '',
        time: formatTimeAgo(c.platform_created_at || c.created_at)
      }));
      setComments(mappedComments);
      setLoading(false);
      setIsSyncing(false);

      // After page renders, silently verify statuses in background
      if (fetchedPosts.some(p => p.status === 'published' && p.platform_post_ids?.facebook)) {
        runBackgroundVerify(fetchedPosts);
      }
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
      setIsSyncing(false);
    }
  };

  const handleManualSync = async () => {
    await fetchDashboardData(true);
  };

  // Silently verify published posts in background — no blocking, updates only status cells
  const runBackgroundVerify = async (currentPosts) => {
    const publishedIds = currentPosts
      .filter(p => p.status === 'published' && p.platform_post_ids?.facebook)
      .map(p => p.id);

    if (publishedIds.length === 0) return;

    // Mark those rows as "checking"
    setVerifyingIds(new Set(publishedIds));
    try {
      const res = await api.post('/api/social-media/posts/verify-status');
      localStorage.setItem(VERIFY_TTL_KEY, Date.now().toString());

      if (res.data?.deleted > 0) {
        // Refresh posts to pick up the updated statuses
        const postsRes = await api.get('/api/social-media/posts?limit=5').catch(() => ({ data: [] }));
        const mappedPosts = (postsRes.data || []).map(post => ({
          ...post,
          date: new Date(post.published_at || post.scheduled_at || post.created_at).toLocaleDateString()
        }));
        setPosts(mappedPosts);
      }
    } catch (_) {
      // Silent fail — don't show errors for background tasks
    } finally {
      setVerifyingIds(new Set());
    }
  };

  const getStatusBadge = (post) => {
    // Check if post was deleted on the platform
    if (post.error_details?.deleted_on_platform) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-800 border-amber-200 shadow-sm">
          <Trash2 size={11} />
          Removed from Platform
        </span>
      );
    }
    const styles = {
      published:        'bg-green-100 text-green-800 border-green-200',
      scheduled:        'bg-blue-100 text-blue-800 border-blue-200',
      draft:            'bg-gray-100 text-gray-800 border-gray-200',
      failed:           'bg-red-100 text-red-800 border-red-200',
      partially_failed: 'bg-orange-100 text-orange-800 border-orange-200',
    };
    const labels = {
      published:        'Published',
      scheduled:        'Scheduled',
      draft:            'Draft',
      failed:           'Failed',
      partially_failed: 'Partial',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[post.status] || styles.draft} capitalize shadow-sm`}>
        {labels[post.status] || post.status}
      </span>
    );
  };

  const getPlatformIcon = (platform, size = 16) => {
    switch (platform) {
      case 'facebook': return <Facebook size={size} className="text-blue-600" />;
      case 'instagram': return <Instagram size={size} className="text-pink-600" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-8 animate-pulse">
        <div className="h-10 bg-gray-200 rounded-lg w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 h-96 bg-gray-200 rounded-xl"></div>
          <div className="h-96 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-8 font-sans">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Social Media Marketing</h1>
          <p className="text-gray-500 mt-1">Manage your brand presence across all platforms</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/admin/social-media/compose')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 flex items-center gap-2 font-medium text-sm"
          >
            <PenSquare size={18} />
            New Post
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {[
          { label: 'Total Reach', value: stats?.reach?.value, trend: stats?.reach?.trend, icon: Activity, color: 'text-blue-600', bg: 'bg-blue-100' },
          { label: 'Engagement Rate', value: stats?.engagement?.value, trend: stats?.engagement?.trend, icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
          { label: 'Total Followers', value: stats?.followers?.value, trend: stats?.followers?.trend, icon: BarChart3, color: 'text-emerald-600', bg: 'bg-emerald-100' },
          { label: 'Posts This Month', value: stats?.postsMonth?.value, trend: stats?.postsMonth?.trend, icon: CalendarPlus, color: 'text-amber-600', bg: 'bg-amber-100' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/80 backdrop-blur-md rounded-2xl p-6 shadow-sm border border-gray-100/50 hover:shadow-md transition-shadow relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/0 to-gray-50 rounded-full translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500 ease-out"></div>
            <div className="flex justify-between items-start relative z-10">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{stat.label}</p>
                <h3 className="text-3xl font-bold text-gray-900">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl ${stat.bg}`}>
                <stat.icon className={stat.color} size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 relative z-10">
              <span className={`flex items-center text-sm font-semibold ${stat.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stat.trend >= 0 ? <TrendingUp size={16} className="mr-1" /> : <TrendingDown size={16} className="mr-1" />}
                {Math.abs(stat.trend)}%
              </span>
              <span className="text-sm text-gray-400">vs last month</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Recent Posts Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white/50 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900">Recent Posts</h2>
                <button
                  onClick={handleManualSync}
                  disabled={isSyncing || loading}
                  className={`p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-100 bg-white transition-all shadow-sm ${
                    isSyncing ? 'cursor-not-allowed opacity-75' : 'hover:scale-105'
                  }`}
                  title="Sync latest posts and metrics"
                >
                  <RefreshCw size={14} className={isSyncing ? 'animate-spin text-blue-600' : ''} />
                </button>
              </div>
              <button 
                onClick={() => navigate('/admin/social-media/calendar')}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Post</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Platforms</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Performance</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {posts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/social-media/compose?edit=${post.id}`)}>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900 font-medium truncate max-w-xs">{post.content}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {post.platforms.map(p => (
                            <span key={p} className="p-1.5 bg-gray-100 rounded-md">
                              {getPlatformIcon(p, 16)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {post.status === 'published' ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-gray-900">{post.reach} reach</span>
                            <span className="text-xs text-gray-400">{post.engagement} eng.</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {verifyingIds.has(post.id) && post.status === 'published' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-500 border-gray-200">
                            <Loader2 size={11} className="animate-spin" />
                            Checking…
                          </span>
                        ) : (
                          getStatusBadge(post)
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {post.date}
                      </td>
                    </tr>
                  ))}
                  {posts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                          <LayoutTemplate size={48} className="text-gray-300 mb-4" />
                          <p className="text-lg font-medium text-gray-900">No posts yet</p>
                          <p className="text-sm mt-1">Create your first post to get started.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Create Post', icon: PenSquare, color: 'text-blue-600', bg: 'bg-blue-50', link: '/admin/social-media/compose' },
              { label: 'Calendar', icon: CalendarPlus, color: 'text-purple-600', bg: 'bg-purple-50', link: '/admin/social-media/calendar' },
              { label: 'Analytics', icon: BarChart3, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/admin/social-media/analytics' },
              { label: 'Templates', icon: LayoutTemplate, color: 'text-amber-600', bg: 'bg-amber-50', link: '/admin/social-media/templates' },
            ].map((action, i) => (
              <button 
                key={i}
                onClick={() => navigate(action.link)}
                className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all flex flex-col items-center justify-center gap-3 group"
              >
                <div className={`p-4 rounded-xl ${action.bg} group-hover:scale-110 transition-transform duration-300`}>
                  <action.icon className={action.color} size={28} />
                </div>
                <span className="font-medium text-gray-700 text-sm">{action.label}</span>
              </button>
            ))}
          </div>

        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-8">
          
          {/* Connected Accounts */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-gray-900">Connected Accounts</h2>
              <button onClick={() => navigate('/admin/social-media/connect')} className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors">
                Manage
              </button>
            </div>
            
            {accounts.length > 0 ? (
              <div className="space-y-4">
                {accounts.map((acc, i) => (
                  <div key={i} className="flex items-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                    <img src={acc.profile_picture_url || `https://ui-avatars.com/api/?name=${acc.account_name || 'Social'}&background=random`} alt={acc.account_name} className="w-12 h-12 rounded-full shadow-sm" />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{acc.account_name}</h4>
                        {getPlatformIcon(acc.platform, 14)}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{acc.followers || 0} followers</p>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 text-center border border-blue-100/50">
                <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                  <Users className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">No accounts connected</h3>
                <p className="text-sm text-gray-500 mb-4">Connect Facebook or Instagram to start posting</p>
                <button 
                  onClick={() => navigate('/admin/social-media/connect')}
                  className="w-full bg-white border border-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                >
                  Connect Now
                </button>
              </div>
            )}
          </div>

          {/* Recent Comments */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-50 rounded-full blur-3xl -z-10 translate-x-10 -translate-y-10"></div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-gray-900">Recent Comments</h2>
              <button 
                onClick={() => navigate('/admin/social-media/comments')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                View All
              </button>
            </div>
            
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="group relative p-4 rounded-xl border border-gray-100 bg-white hover:border-purple-200 hover:shadow-md transition-all cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="relative flex-shrink-0">
                      {comment.avatar ? (
                        <img src={comment.avatar} alt={comment.author} className="w-10 h-10 rounded-full ring-2 ring-white shadow-sm object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full ring-2 ring-white shadow-sm bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm uppercase">
                          {comment.author.charAt(0)}
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                        {getPlatformIcon(comment.platform, 12)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm truncate">{comment.author}</h4>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{comment.time}</span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{comment.text}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {comments.length === 0 && (
                <div className="text-center py-8">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium text-sm">No new comments</p>
                  <p className="text-gray-400 text-xs mt-1">You're all caught up!</p>
                </div>
              )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}
